import { ButtonTooltip } from "@admin/components/molecules/Tooltips";
import {
  Availability,
  calculateShiftDuration,
  CompanyLocation,
  dateToISO,
  getAvailabilitiesByDate,
  getHoursAndMinutes,
  getLocationUsers,
  getShiftsByDate,
  LocationUser,
  Shift,
} from "@enos-monorepo/lib";
import { useQuery } from "@tanstack/react-query";
import { addDays, getDay, isBefore, isSameDay, startOfToday } from "date-fns";
import { useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { NewOpenShiftDailyModal } from "../../../components";
import {
  AddMultiPersonnelShiftModal,
  AvailabilityInfo,
  Day,
  Header,
  ShiftHours,
  TotalAmount,
} from "..";
import { ClearThisDaysShiftsModal } from "../Modals/ClearThisDaysShiftsModal";
import { EditOpenShiftModal } from "../Modals/EditOpenShiftModal";
import { AddNewShiftToSinglePersonnelModal } from "./Modals/AddNewShiftToSinglePersonnelModal";
import { CopyShiftsDayModal } from "./Modals/CopyShiftsDayModal";
import { EditNormalShiftModal } from "./Modals/EditNormalShiftModal";

interface ShiftTableProps {
  selectedLocation?: CompanyLocation;
  companyId: string;
}
export interface UserShiftAndAvailabilitySchema {
  users?: {
    [userId: string]: {
      userInfo: LocationUser;
      shifts: {
        [day: string]: Shift | undefined;
      };
      availabilities: {
        [day: string]: Availability | undefined;
      };
    };
  };
  openShifts?: {
    [day: string]: Shift[];
  };
}

export function ShiftTable({ selectedLocation, companyId }: ShiftTableProps) {
  const { t } = useTranslation();
  const [selectedWeek, setSelectedWeek] = useState<DateRange>();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isNewOpenShiftDailyModal, setNewOpenShiftDailyModal] = useState(false);
  const [isEditOpenShiftModal, setEditOpenShiftModal] = useState(false);
  const [isAvailabilityVisible, setAvailabilityVisible] = useState(false);
  const [isAddShiftDayModalOpen, setIsAddShiftDayModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<LocationUser>();
  const [selectedShift, setSelectedShift] = useState<Shift>();
  const [selectedOpenShift, setSelectedOpenShift] = useState<Shift>();
  const [
    openAddNewShiftToSinglePersonnelModal,
    setOpenAddNewShiftToSinglePersonnelModal,
  ] = useState(false);
  const [isClearDayShiftsModalOpen, setClearDayShiftsModalOpen] =
    useState(false);
  const [openEditNormalShiftModal, setOpenEditNormalShiftModal] =
    useState(false);
  const [isCopyShiftsDayModalOpen, setIsCopyShiftsDayModalOpen] =
    useState(false);
  const [isPastTooltipOpen, setIsPastTooltipOpen] = useState(false);
  const [isTodayTooltipOpen, setIsTodayTooltipOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const { data: shifts, isLoading } = useQuery<Shift[]>({
    queryKey: [
      "shifts",
      selectedLocation?.id,
      selectedWeek?.from,
      selectedWeek?.to,
    ],
    queryFn: () =>
      getShiftsByDate({
        LocationId: selectedLocation?.id ?? "",
        StartDate: selectedWeek?.from ? dateToISO(selectedWeek.from) : "",
        EndDate: selectedWeek?.to ? dateToISO(selectedWeek?.to) : "",
      }),
    enabled: !!selectedLocation && !!selectedWeek,
  });

  const navigate = useNavigate();

  const { data: availabilities } = useQuery<Availability[]>({
    queryKey: [
      "availabilities",
      selectedLocation?.id,
      selectedWeek?.from,
      selectedWeek?.to,
    ],
    queryFn: () =>
      getAvailabilitiesByDate({
        LocationId: selectedLocation?.id ?? "",
        startDateTime: selectedWeek?.from ? dateToISO(selectedWeek.from) : "",
        endDateTime: selectedWeek?.to ? dateToISO(selectedWeek?.to) : "",
      }),
    enabled: !!selectedLocation && !!selectedWeek,
  });

  const { data: locationUsers } = useQuery<LocationUser[]>({
    queryKey: ["companyLocations", selectedLocation?.id],
    queryFn: () => getLocationUsers(selectedLocation?.id ?? ""),
    enabled: !!selectedLocation,
  });

  const daysOfWeek = useMemo(
    () => [
      t("shift.Monday"),
      t("shift.Tuesday"),
      t("shift.Wednesday"),
      t("shift.Thursday"),
      t("shift.Friday"),
      t("shift.Saturday"),
      t("shift.Sunday"),
    ],
    [t],
  );

  // Combined schema for grouping shifts and availabilities by user and day
  const shiftSchema = useMemo(() => {
    if (!locationUsers || (!shifts && !availabilities)) return {};

    const schema: UserShiftAndAvailabilitySchema = {
      users: {},
      openShifts: {},
    };

    // Initialize users in the schema
    locationUsers
      .filter((user) => !user.isDeleted)
      .forEach((user) => {
        if (schema.users === undefined) return;
        schema.users[user.userId] = {
          userInfo: user,
          shifts: {
            Monday: undefined,
            Tuesday: undefined,
            Wednesday: undefined,
            Thursday: undefined,
            Friday: undefined,
            Saturday: undefined,
            Sunday: undefined,
          },
          availabilities: {
            Monday: undefined,
            Tuesday: undefined,
            Wednesday: undefined,
            Thursday: undefined,
            Friday: undefined,
            Saturday: undefined,
            Sunday: undefined,
          },
        };
      });

    // Process shifts
    shifts?.forEach((shift) => {
      const userId = shift.userId;
      const dayOfWeek = shift.plannedStartDate
        ? getDay(shift.plannedStartDate)
        : -1;
      const dayName =
        dayOfWeek === 0 ? daysOfWeek[6] : daysOfWeek[dayOfWeek - 1];

      if (userId && schema.users?.[userId] && dayName) {
        schema.users[userId].shifts[dayName] = shift;
      } else if (dayName && schema.openShifts) {
        schema.openShifts[dayName] = schema.openShifts[dayName] || [];
        schema.openShifts[dayName].push(shift);
      }
    });

    // Process availabilities
    availabilities?.forEach((availability) => {
      const userId = availability.userId;
      const dayOfWeek = getDay(availability.startDateTime); // Get the day of the week from availability start
      const dayName =
        dayOfWeek === 0 ? daysOfWeek[6] : daysOfWeek[dayOfWeek - 1];

      if (userId && schema.users?.[userId] && dayName) {
        schema.users[userId].availabilities[dayName] = availability;
      }
    });

    return schema;
  }, [daysOfWeek, locationUsers, shifts, availabilities]);

  const maxOpenShifts = useMemo(
    () =>
      Math.max(
        ...daysOfWeek.map((day) => shiftSchema.openShifts?.[day]?.length || 0),
      ),
    [daysOfWeek, shiftSchema.openShifts],
  );

  const getSelectedDay = (dayIndex: number) => {
    if (selectedWeek?.from && selectedWeek?.to) {
      const date = new Date(selectedWeek.from);
      date.setDate(date.getDate() + dayIndex);
      setSelectedDate(date);
    }
  };

  const handleAddSingleShift = (dayIndex: number, userInfo: LocationUser) => {
    setSelectedUser(userInfo);
    getSelectedDay(dayIndex);
    setOpenAddNewShiftToSinglePersonnelModal(true);
  };

  const handleEditShift = (shift: Shift) => () => {
    setSelectedShift(shift);
    setOpenEditNormalShiftModal(true);
  };

  const handleEditOpenShift = (shift: Shift) => () => {
    setSelectedOpenShift(shift);
    setEditOpenShiftModal(true);
  };

  const handleTooltip = (
    event: React.MouseEvent<HTMLTableCellElement>,
    type: "past" | "today",
  ) => {
    const targetElement = event.currentTarget;
    const rect = targetElement.getBoundingClientRect();

    setTooltipPosition({
      top: rect.bottom + window.scrollY - 100,
      left: rect.left + window.scrollX - 450,
    });

    if (type === "past") setIsPastTooltipOpen(true);
    if (type === "today") setIsTodayTooltipOpen(true);
  };

  return (
    <>
      <div className="card card-grid min-w-full mt-7.5">
        <Header
          locationId={selectedLocation?.id ?? ""}
          companyId={companyId}
          personnels={locationUsers}
          shiftsLength={shifts?.length ?? 0}
          selectedWeek={selectedWeek}
          setSelectedWeek={setSelectedWeek}
          setAvalibilityVisible={setAvailabilityVisible}
          shifts={shifts}
        />

        <table className="table table-border table-fixed align-middle w-full">
          <thead>
            <tr>
              <th className="w-[213px]">{t("common.Personnel")}</th>
              {daysOfWeek.map((day, index) => {
                const dayDate = new Date(selectedWeek?.from ?? "");
                dayDate.setDate(dayDate.getDate() + index);
                const isSame = isSameDay(dayDate, startOfToday());
                return (
                  <th
                    className={`text-center ${isSame ? "!bg-[#78829D] !text-[#F1F1F4]" : ""}  relative`}
                    key={day}
                  >
                    <Day
                      setAddModal={setIsAddShiftDayModalOpen}
                      setSelectedDay={setSelectedDate}
                      setAddOpenModal={setNewOpenShiftDailyModal}
                      setClearDayShiftsModal={setClearDayShiftsModalOpen}
                      date={
                        selectedWeek?.from
                          ? addDays(selectedWeek.from, index)
                          : undefined
                      }
                      setCopyShiftsDayModal={setIsCopyShiftsDayModalOpen}
                    />
                  </th>
                );
              })}
              <th className="w-[120px]">{t("shift.Total Amount")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9}>
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent bg-white"></div>
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {shiftSchema.users &&
                  Object.entries(shiftSchema.users).map(
                    ([userId, { userInfo, shifts, availabilities }]) => {
                      let totalAmount = 0; // Her kullanıcı için vardiya süresi toplamı
                      return (
                        <tr key={userId}>
                          <td>
                            <span className="font-[500] text-[#252F4A]">
                              {userInfo.firstName} {userInfo.lastName}
                            </span>
                            <span className="font-normal">
                              <br />@{userInfo.userName}
                            </span>
                          </td>

                          {daysOfWeek.map((day, index) => {
                            const shift = shifts[day];
                            const availability = availabilities[day];

                            const shiftDuration = shift
                              ? calculateShiftDuration(
                                  getHoursAndMinutes(shift.plannedStartDate),
                                  getHoursAndMinutes(shift.plannedEndDate),
                                  shift.breakDuration,
                                )
                              : 0;
                            totalAmount += shiftDuration;

                            const dayDate = new Date(selectedWeek?.from ?? "");
                            dayDate.setDate(dayDate.getDate() + index);
                            const isPast = isBefore(dayDate, startOfToday());
                            const isSame = isSameDay(dayDate, startOfToday());
                            const active = !isSame && !isPast;

                            return (
                              <td
                                key={`${userId}-${day}`}
                                className={`text-center ${isSame ? "bg-[#DBDFE9]" : ""}`}
                                onClick={(e) =>
                                  isPast
                                    ? handleTooltip(e, "past")
                                    : isSame
                                      ? handleTooltip(e, "today")
                                      : undefined
                                }
                              >
                                {shift &&
                                shift.plannedEndDate &&
                                shift.plannedStartDate ? (
                                  <ShiftHours
                                    start={shift.plannedStartDate}
                                    end={shift.plannedEndDate}
                                    opacity={isPast ? 60 : 100}
                                    onClick={
                                      active
                                        ? handleEditShift(shift)
                                        : undefined
                                    }
                                  />
                                ) : (
                                  active && (
                                    <div className="flex gap-2.5 justify-center">
                                      <i
                                        onClick={
                                          !isPast && !isSame
                                            ? () =>
                                                handleAddSingleShift(
                                                  index,
                                                  userInfo,
                                                )
                                            : undefined
                                        }
                                        className={`ki-filled ki-plus-squared text-xl cursor-pointer ${isPast || isSame ? "cursor-not-allowed" : ""}`}
                                      ></i>
                                      {isAvailabilityVisible && (
                                        <span className="text-xs ml-1">
                                          {!shift && active ? (
                                            <AvailabilityInfo
                                              key={
                                                availability?.id +
                                                index.toString()
                                              }
                                              id={availability?.id}
                                              availabilityTypeId={
                                                availability?.availabilityTypeId ??
                                                3
                                              }
                                              startDateTime={
                                                availability?.startDateTime ??
                                                ""
                                              }
                                              endDateTime={
                                                availability?.endDateTime ?? ""
                                              }
                                              reason={availability?.description}
                                            />
                                          ) : (
                                            ""
                                          )}
                                        </span>
                                      )}
                                    </div>
                                  )
                                )}
                              </td>
                            );
                          })}
                          <td className="text-center">
                            <TotalAmount totalAmount={totalAmount} />
                          </td>
                        </tr>
                      );
                    },
                  )}

                <tr>
                  <td>
                    <div className="font-[500] flex flex-col gap-2">
                      <span>{t("shift.Open Shifts")}</span>
                    </div>
                  </td>

                  {daysOfWeek.map((day, index) => {
                    const openShifts = shiftSchema.openShifts?.[day] || [];
                    const dayDate = new Date(selectedWeek?.from ?? "");
                    dayDate.setDate(dayDate.getDate() + index);
                    const isSame = isSameDay(dayDate, startOfToday());
                    const isPast = isBefore(dayDate, startOfToday());
                    return (
                      <td
                        key={day}
                        className={`!p-0 align-top h-fit w-full ${isSame ? "bg-[#DBDFE9]" : ""}`}
                      >
                        {openShifts.length > 0 && (
                          <div className="flex flex-col w-full">
                            {openShifts.map((openShift, i) => (
                              <div
                                key={i}
                                className="pt-4 w-full  flex flex-col"
                              >
                                <ShiftHours
                                  isOpen
                                  start={openShift.plannedStartDate}
                                  end={openShift.plannedEndDate}
                                  opacity={isPast ? 60 : 100}
                                  onClick={
                                    !isSame && !isPast
                                      ? handleEditOpenShift(openShift)
                                      : undefined
                                  }
                                />

                                <div className="border-b border-b-gray-200 pt-2.5 w-full"></div>
                              </div>
                            ))}
                          </div>
                        )}
                        {Array.from(
                          { length: maxOpenShifts - openShifts.length },
                          (_, index) => (
                            <div key={index} className="w-full h-[66px]">
                              {index !== 0 && (
                                <div className="border-b border-b-gray-200 w-full"></div>
                              )}
                            </div>
                          ),
                        )}
                      </td>
                    );
                  })}
                  <td className="text-center !p-0">
                    {Array.from({ length: maxOpenShifts - 1 }, (_, index) => (
                      <div key={index} className="w-full h-full">
                        <div className="border-b border-b-gray-200 mb-[1px] w-full"></div>
                      </div>
                    ))}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <EditOpenShiftModal
        open={isEditOpenShiftModal}
        onClose={() => setEditOpenShiftModal(false)}
        personnelList={locationUsers?.filter((user) => !user.isDeleted)}
        shift={selectedOpenShift}
        companyId={companyId}
      />
      <NewOpenShiftDailyModal
        selectedDate={selectedDate}
        open={isNewOpenShiftDailyModal}
        onClose={() => setNewOpenShiftDailyModal(false)}
        companyId={companyId}
        companyLocationId={selectedLocation?.id as string}
      />
      <AddMultiPersonnelShiftModal
        open={isAddShiftDayModalOpen}
        onClose={() => setIsAddShiftDayModalOpen(false)}
        selectedDate={selectedDate}
        locationUsers={locationUsers?.filter((user) => !user.isDeleted)}
        companyId={companyId}
        locationId={selectedLocation?.id ?? ""}
      />
      <CopyShiftsDayModal
        open={isCopyShiftsDayModalOpen}
        onClose={() => setIsCopyShiftsDayModalOpen(false)}
        personnelList={locationUsers?.filter((user) => !user.isDeleted)}
        day={selectedDate}
        allShifts={shifts}
        companyId={companyId}
      />
      <AddNewShiftToSinglePersonnelModal
        open={openAddNewShiftToSinglePersonnelModal}
        onClose={() => setOpenAddNewShiftToSinglePersonnelModal(false)}
        personnel={selectedUser}
        companyId={companyId}
        selectedDate={selectedDate}
      />
      <EditNormalShiftModal
        open={openEditNormalShiftModal}
        onClose={() => setOpenEditNormalShiftModal(false)}
        personnelList={locationUsers?.filter((user) => !user.isDeleted)}
        shift={selectedShift}
        companyId={companyId}
      />
      <ClearThisDaysShiftsModal
        open={isClearDayShiftsModalOpen}
        onClose={() => setClearDayShiftsModalOpen(false)}
        date={selectedDate}
        shiftsWillBeDeleted={shifts?.filter(
          (shift) =>
            shift?.plannedStartDate &&
            isSameDay(new Date(shift.plannedStartDate), selectedDate),
        )}
      />
      <ButtonTooltip
        id={"today-tooltip"}
        position={tooltipPosition}
        isOpen={isTodayTooltipOpen}
        onClose={() => setIsTodayTooltipOpen(false)}
        title={t("shift.You cannot edit today’s shifts here")}
        description={t(
          "shift.To add and edit shifts for today, please use Today’s Shifts screen",
        )}
        buttons={[
          {
            children: t("shift.Go to Today’s Shifts"),
            onClick: () => navigate("/shift-management/todays-shifts"),

            variant: "secondary",
          },
        ]}
      />
      <ButtonTooltip
        id={"past-tooltip"}
        position={tooltipPosition}
        isOpen={isPastTooltipOpen}
        onClose={() => setIsPastTooltipOpen(false)}
        title={t("shift.You cannot plan shifts for previous days")}
        description={t(
          "shift.To add and edit past shifts, please use Past Shifts screen",
        )}
        buttons={[
          {
            children: t("shift.Go to Past Shifts"),
            onClick: () => navigate("/shift-management/past-shifts"),
            variant: "secondary",
          },
        ]}
      />
    </>
  );
}
